<template>
  <v-dialog
    v-model="dialog"
    width="850"
    :fullscreen="$vuetify.display.smAndDown"
    persistent
  >
    <template #activator="{ props }">
      <slot
        name="activator"
        v-bind="{ props, status, loading }"
      />
    </template>
    <v-card>
      <v-card-title class="pa-0">
        <v-toolbar color="primary">
          <v-toolbar-title>
            {{ $t('connection-manager.google-reviews.dialog-title') }}
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-btn
              icon
              @click="dialog = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
      </v-card-title>
      <v-card-text>
        <GoogleReviewLinkInput
          :company-id="companyId"
          @success="handleSaved"
        />
        <v-divider class="my-4" />
        <v-row>
          <v-col
            cols="12"
            sm="6"
          >
            <p class="text-primary font-weight-bold">
              {{ $t('connection-manager.google-reviews.choose-title') }}
            </p>
            <p>
              {{ $t('connection-manager.google-reviews.choose-description') }}
            </p>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            class="d-flex flex-column ga-2"
          >
            <div class="d-flex justify-space-between align-center w-full border px-4">
              <p class="text-body-2 mb-0">
                {{ $t('connection-manager.google-reviews.include-in-signature') }}
              </p>
              <v-switch
                v-model="includeInSignature"
                :loading="toggleLoading"
                :disabled="!localReviewLink"
                color="primary"
                density="compact"
                hide-details
              />
            </div>
            <div class="d-flex justify-space-between align-center w-full border px-4">
              <p class="text-body-2 mb-0">
                {{ $t('connection-manager.google-reviews.include-in-contact-details') }}
              </p>
              <v-switch
                v-model="includeInContactDetails"
                :loading="contactDetailsLoading"
                :disabled="!localReviewLink"
                color="primary"
                density="compact"
                hide-details
              />
            </div>
            <p class="mb-0 text-info">
              {{ $t('connection-manager.google-reviews.note') }}
            </p>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import { computed, inject, onMounted, ref, watch } from 'vue'

import type { AuthInstance } from '@/auth'
import GoogleReviewLinkInput from '@/components/GoogleReviewLinkInput.vue'
import GET_GOOGLE_REVIEW_SETTINGS from '@/modules/settings/emailSettings/queries/GetGoogleReviewSettings.gql'
import UPDATE_COMPANY_AMBASSADOR_SIGNATURE_INCLUDE_GOOGLE_REVIEW from '@/modules/settings/emailSettings/queries/UpdateCompanyAmbassadorSignatureIncludeGoogleReview.gql'
import UPDATE_GOOGLE_REVIEW_ON_LANDINGPAGE from '@/modules/settings/emailSettings/queries/UpdateGoogleReviewOnLandingpage.gql'
import CompanyQuery from '@/modules/settings/functionalSettings/queries/Company.gql'

type ConnectionStatus = 'not-connected' | 'link-connected' | 'link-active'

const $auth = inject<AuthInstance>('auth')
const companyId = computed(() => $auth?.user?.companyId ?? '')

const dialog = ref(false)
const localReviewLink = ref('')
const toggleLoading = ref(false)
const localShowOnLandingpage = ref(false)
const localIncludeInSignature = ref(false)

const { load: loadSettings, onResult: onSettingsResult } = useLazyQuery(
  GET_GOOGLE_REVIEW_SETTINGS,
  () => ({ companyId: companyId.value })
)

onSettingsResult(({ data }) => {
  if (data?.settings) {
    localReviewLink.value = data.settings.company.googleReviewLink
    localShowOnLandingpage.value = data.settings.company.showGoogleReviewOnLandingpage ?? false
  }
})

const {
  result: companyResult,
  load: loadCompany,
  refetch: refetchCompany,
} = useLazyQuery(CompanyQuery, () => ({
  id: companyId.value,
}))

watch(
  () => companyResult.value,
  (value) => {
    const data = value?.data ?? value
    localIncludeInSignature.value =
      data?.company?.ambassador?.signature?.includeGoogleReview ?? false
  },
  { immediate: true }
)

const { mutate: updateIncludeGoogleReview } = useMutation(
  UPDATE_COMPANY_AMBASSADOR_SIGNATURE_INCLUDE_GOOGLE_REVIEW
)

const { mutate: updateShowOnLandingpage, loading: contactDetailsLoading } = useMutation(
  UPDATE_GOOGLE_REVIEW_ON_LANDINGPAGE
)

const includeInSignature = computed({
  get: () => localIncludeInSignature.value,
  set: (val) => updateToggle(val),
})

const includeInContactDetails = computed({
  get: () => localShowOnLandingpage.value,
  set: (val) => updateContactDetailsToggle(val),
})

const status = computed<ConnectionStatus>(() => {
  if (!localReviewLink.value) {
    return 'not-connected'
  }
  if (localIncludeInSignature.value || localShowOnLandingpage.value) {
    return 'link-active'
  }
  return 'link-connected'
})

const loading = computed(() => false)

watch(dialog, (isOpen) => {
  if (isOpen && companyId.value) {
    loadSettings()
    loadCompany()
  }
})

onMounted(() => {
  if (companyId.value) {
    loadSettings()
    loadCompany()
  }
})

const handleSaved = async (reviewLink: string) => {
  localReviewLink.value = reviewLink
  if (!reviewLink) {
    await updateToggle(false)
    await updateContactDetailsToggle(false)
  }
}

const updateToggle = async (value: boolean) => {
  if (!companyId.value) return
  toggleLoading.value = true
  try {
    await updateIncludeGoogleReview({
      input: {
        companyId: companyId.value,
        includeGoogleReview: value,
      },
    })
    localIncludeInSignature.value = value
    await refetchCompany()
  } finally {
    toggleLoading.value = false
  }
}

const updateContactDetailsToggle = async (value: boolean) => {
  if (!companyId.value) return
  try {
    toggleLoading.value = true
    await updateShowOnLandingpage({
      input: {
        companyId: companyId.value,
        showGoogleReviewOnLandingpage: value,
      },
    })
    localShowOnLandingpage.value = value
  } finally {
    toggleLoading.value = false
  }
}
</script>
